/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Check, ChevronsUpDown, LoaderCircle, Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { Input } from "@/components/ui/input";

type ErpSkuProp = {
  placeholder: string;
  value: any;
  bindLabel: string;
  bindValue: string;
  emptyListMessage?: string;
  searchLabel?: string;
  onChange: (prop: string | string[]) => void;
  onSearch?: (prop: string) => Promise<any>;
  onBlur?: () => void;
  defaultValue?: any[];
  onValueChange?: (prop: any) => void;
  onAllValueChange?: (prop: any) => void;
  multiple?: boolean;
  options?: any[];
};

export const ErpSku = ({
  placeholder,
  searchLabel,
  emptyListMessage,
  value,
  onChange,
  onSearch,
  onBlur,
  bindLabel,
  bindValue,
  defaultValue,
  onValueChange,
  onAllValueChange,
  multiple = false,
  options = [],
}: // defaultValue,
ErpSkuProp) => {
  const [searchText, setSearchText] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [process, setProcess] = React.useState(false);
  const [list, setList] = React.useState<any>([]);
  const [visibleCount, setVisibleCount] = React.useState(50);

  React.useEffect(() => {
    setList(options?.length ? options : (defaultValue ?? []));
  }, [options, defaultValue]);

  const filteredList = React.useMemo(() => {
    const source = list ?? [];
    if (!searchText) {
      return source;
    }

    const lowerSearchText = searchText.toLowerCase();
    return source.filter((option: any) => {
      const label = option?.[bindLabel] as string | undefined;
      const valueText = option?.[bindValue] as string | undefined;
      return (
        label?.toLowerCase().includes(lowerSearchText) ||
        valueText?.toLowerCase().includes(lowerSearchText)
      );
    });
  }, [list, searchText, bindLabel, bindValue]);

  React.useEffect(() => {
    setVisibleCount(50);
  }, [filteredList.length, searchText]);

  const displayLabel = React.useMemo(() => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) {
        return undefined;
      }
      return value.map((itemValue: any) => {
        const option =
          list.find((option: any) => option?.[bindValue] === itemValue) ||
          defaultValue?.find(
            (option: any) => option?.[bindValue] === itemValue,
          );
        return option
          ? `${option?.[bindLabel]} / ${option?.[bindValue]}`
          : itemValue;
      });
    }

    const currentValue =
      list.find((option: any) => option?.[bindValue] === value) ||
      defaultValue?.[0];
    return currentValue
      ? `${currentValue?.[bindLabel]} / ${currentValue?.[bindValue]}`
      : value;
  }, [value, list, defaultValue]);

  const onChangeValue = (prop: any) => {
    onAllValueChange?.(prop);

    if (multiple) {
      const existingValues = Array.isArray(value) ? value : [];
      const valueId = prop?.[bindValue];
      const alreadySelected = existingValues.some(
        (item: any) => item === valueId,
      );
      const nextValues = alreadySelected
        ? existingValues.filter((item: any) => item !== valueId)
        : [...existingValues, valueId];
      onValueChange?.(prop);
      onChange(nextValues);
    } else {
      onValueChange?.(prop?.[bindLabel]);
      onChange(prop?.[bindValue]);
    }
  };

  React.useEffect(() => {
    if (searchText !== "" && onSearch) {
      onSearchHandle();
    }
  }, [searchText, onSearch]);

  const onSearchHandle = async () => {
    if (!onSearch) {
      return;
    }

    setList([]);
    setProcess(true);
    const response: any = await onSearch(searchText);
    setProcess(false);
    if (response?.data) {
      setList(response.data);
    }
  };

  const visibleOptions = React.useMemo(
    () => filteredList.slice(0, visibleCount),
    [filteredList, visibleCount],
  );

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (
      target.scrollTop + target.clientHeight >= target.scrollHeight - 20 &&
      visibleCount < filteredList.length
    ) {
      setVisibleCount((prev) => Math.min(prev + 50, filteredList.length));
    }
  };

  const shouldShowDisplayLabel = Array.isArray(displayLabel)
    ? displayLabel.length > 0
    : Boolean(displayLabel);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild onBlur={onBlur}>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-auto min-h-[40px] justify-between"
          >
            <span className="flex-1 text-left">
              {multiple &&
              Array.isArray(displayLabel) &&
              displayLabel.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {displayLabel.map((label, idx) => (
                    <span
                      key={idx}
                      className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700"
                    >
                      {label}
                    </span>
                  ))}
                </span>
              ) : shouldShowDisplayLabel ? (
                displayLabel
              ) : (
                <span className="opacity-50">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <div className="p-1">
              <div className="flex">
                <Button
                  type="button"
                  onClick={() => {
                    onSearchHandle();
                  }}
                  className="absolute"
                  size={"icon"}
                  variant={"ghost"}
                >
                  <Search />
                </Button>
                <Input
                  className="!outline-none !border-none pl-10 focus:ring-0 focus:ring-offset-0"
                  onChange={(e) => setSearchText(e.target.value)}
                  value={searchText}
                  placeholder={searchLabel || "Search Options"}
                />
              </div>
            </div>

            <CommandList onScroll={handleScroll}>
              <ConditionRenderer condition={process}>
                <div className="flex items-center justify-center">
                  <LoaderCircle className="mr-2 h-6 w-6 animate-spin" />
                </div>
              </ConditionRenderer>
              <ConditionRenderer
                condition={!process && visibleOptions.length === 0}
              >
                <div className="text-sm px-3 pt-3">
                  {emptyListMessage || "No Options found."}
                </div>
              </ConditionRenderer>
              <CommandGroup>
                {visibleOptions.map((option: any) => (
                  <CommandItem
                    key={option?.[bindValue]}
                    value={option}
                    onSelect={() => {
                      onChangeValue(option);
                      if (!multiple) {
                        setOpen(false);
                      }
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        multiple && Array.isArray(value)
                          ? value.includes(option?.[bindValue])
                            ? "opacity-100"
                            : "opacity-0"
                          : value === option?.[bindValue]
                            ? "opacity-100"
                            : "opacity-0",
                      )}
                    />
                    {option?.[bindLabel] || ""}
                    {option?.[bindLabel] && option?.[bindValue] ? " / " : ""}
                    {option?.[bindValue]}
                    {option?.["erp_uom"] ? " / " + option?.["erp_uom"] : ""}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
